import type { ClientChatMessage } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, useEffect, useMemo, useState, type FC } from 'react';
import type {
  ListRenderItem,
  ViewabilityConfigCallbackPairs,
  ViewToken
} from 'react-native';
import { FlatList, View } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useGetChatMessages } from '@/features/api/hooks/communication-chat/useGetChatMessages';
import { usePendingChatMessages } from '@/features/api/hooks/communication-chat-events/usePendingChatMessages';
import { useSetChatRead } from '@/features/api/hooks/communication-chat-events/useSetChatRead';
import { ChatMessage } from '@/features/chats/components/ChatMessage';
import { DaySeparator } from '@/features/chats/components/DaySeparator';
import { EmptyChatState } from '@/features/chats/components/EmptyChatState';
import { needsSeparator } from '@/features/chats/utils/message';
import { webScrollBarStyle } from '@/styles/customStyles';

type Props = { chatId: string; deleted?: boolean };

export const ChatMessages: FC<Props> = ({ chatId, deleted }) => {
  const {
    data: serverMessages = [],
    isSuccess,
    isFetchingNextPage,
    fetchNextPage,
    isRefetching
  } = useGetChatMessages(chatId);

  const pendingMessages = usePendingChatMessages(chatId);

  const messages = useMemo((): ClientChatMessage[] => {
    const messages = [...pendingMessages, ...serverMessages];
    return messages;
  }, [serverMessages, pendingMessages]);

  const { mutate: setChatRead } = useSetChatRead(chatId);
  const [lastReadTime, setLastReadTime] = useState<number>();

  useEffect(() => {
    if (lastReadTime) {
      setChatRead({ readTime: lastReadTime });
    }
  }, [lastReadTime, setChatRead]);

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken<ClientChatMessage>[] }) => {
      const createdTimes = viewableItems
        .map((token) => token.item.created)
        .filter((sentTime) => sentTime !== undefined);
      const maxCreatedTime = Math.max(...createdTimes);
      setLastReadTime((oldTime) => {
        if (oldTime) {
          return Math.max(oldTime, maxCreatedTime);
        } else {
          return maxCreatedTime;
        }
      });
    },
    []
  );

  const viewabilityConfigCallbackPairs =
    useMemo<ViewabilityConfigCallbackPairs>(
      () => [
        {
          onViewableItemsChanged,
          viewabilityConfig: {
            minimumViewTime: 500,
            viewAreaCoveragePercentThreshold: 90,
            waitForInteraction: false
          }
        }
      ],
      [onViewableItemsChanged]
    );

  const renderItem: ListRenderItem<ClientChatMessage> = useCallback(
    ({ item, index }) => {
      if (needsSeparator(messages, index)) {
        return (
          <View>
            <DaySeparator time={item.sentTime ?? 0} />
            <ChatMessage message={item} />
          </View>
        );
      } else {
        return <ChatMessage message={item} />;
      }
    },
    [messages]
  );

  const extractKey = useCallback(
    (message: ClientChatMessage): string => message.id,
    []
  );

  const fetchNext = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const { bottom } = useSafeAreaInsets();

  const showEmpty = isSuccess && messages.length === 0;
  if (showEmpty) {
    return <EmptyChatState />;
  }

  return (
    <FlatList<ClientChatMessage>
      data={messages}
      extraData={messages}
      keyExtractor={extractKey}
      inverted
      renderItem={renderItem}
      // Fixes "Cannot remove child at index X from parent Viewgroup[Y], only Z children in parent." error on Android.
      // For details see https://www.linkedin.com/pulse/fixing-cannot-remove-child-index-x-from-parent-only-z-jatin-sharma-198ac
      removeClippedSubviews={false}
      ListFooterComponent={
        isFetchingNextPage ? <ActivityIndicator animating /> : undefined
      }
      refreshing={
        isRefetching || isFetchingNextPage || pendingMessages.length > 0
      }
      onEndReached={fetchNext}
      viewabilityConfigCallbackPairs={viewabilityConfigCallbackPairs}
      keyboardShouldPersistTaps="handled"
      style={[webScrollBarStyle, { paddingTop: deleted ? bottom : 0 }]}
    />
  );
};
